import { useLazyQuery, useQuery } from '@apollo/client';
import type { FlagQueryFilterForGrid } from '@generated/graphql';
import { usePageState } from '@utils/usePageState';
import { useSnackbar } from 'hephaestus/web';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReconFlagsFiltersCardProps } from '../filters/reconFlagsFiltersCard.types';
import {
  FETCH_FLAG_TYPES,
  SEARCH_USERS_FOR_RECON_FLAGS_GRID,
} from './flags.queries';
import {
  RECON_FLAGS_MIN_SEARCH_LENGTH,
  RECON_FLAGS_SEARCH_DEBOUNCE_MS,
} from './reconFlags.config';
import type { ReconFlagSeverity } from './reconFlags.types';
import {
  buildReconFlagsAccountIdsFromClientSelection,
  buildSearchUsersForReconFlagsVariables,
  mapReconFlagsUserSearchHits,
  type ReconFlagsClientOption,
} from './reconFlagsClientSearch';
import {
  hasReconFlagsActiveFilters,
  RECON_FLAGS_DEFAULT_FILTER_VALUES,
} from './reconFlagsFilterDefaults';
import {
  buildReconFlagsGridFilter,
  parseReconFlagsSeverityTab,
} from './reconFlagsGridVariables';

type UseReconFlagsFiltersInput = {
  orgId: string | undefined;
  setPage: (page: number) => void;
};

export const useReconFlagsFilters = ({
  orgId,
  setPage,
}: UseReconFlagsFiltersInput): {
  gridFilter: FlagQueryFilterForGrid | null;
  filtersProps: Omit<ReconFlagsFiltersCardProps, 'theme'>;
} => {
  const { t } = useTranslation(['reconciliation']);
  const { enqueueSnackbar } = useSnackbar();
  const [flagState, setFlagState] = usePageState(
    RECON_FLAGS_DEFAULT_FILTER_VALUES.flagState,
    'rfState',
  );
  const [flagType, setFlagType] = usePageState<string | undefined>(
    RECON_FLAGS_DEFAULT_FILTER_VALUES.flagType,
    'rfType',
  );
  const [severityTabStored, setSeverityTabStored] = usePageState(
    RECON_FLAGS_DEFAULT_FILTER_VALUES.severityTab,
    'rfSev',
  );
  const severityTab = useMemo(
    () => parseReconFlagsSeverityTab(severityTabStored),
    [severityTabStored],
  );
  const [categoryFilter, setCategoryFilter] = usePageState<string>(
    RECON_FLAGS_DEFAULT_FILTER_VALUES.categoryFilter,
    'rfCat',
  );
  const [agingFilter, setAgingFilter] = usePageState<string>(
    RECON_FLAGS_DEFAULT_FILTER_VALUES.agingFilter,
    'rfAge',
  );
  const [selectedClient, setSelectedClient] =
    useState<ReconFlagsClientOption | null>(null);

  const [runUserSearch] = useLazyQuery(SEARCH_USERS_FOR_RECON_FLAGS_GRID, {
    fetchPolicy: 'no-cache',
  });

  const onSearchClients = useCallback(
    async (query: string): Promise<ReconFlagsClientOption[]> => {
      const trimmed = query.trim();
      if (!orgId || trimmed.length < RECON_FLAGS_MIN_SEARCH_LENGTH) {
        return [];
      }
      const result = await runUserSearch({
        variables: buildSearchUsersForReconFlagsVariables(orgId, trimmed),
      });
      if (result.error) {
        throw result.error;
      }
      return mapReconFlagsUserSearchHits(result.data?.searchUsers?.users);
    },
    [orgId, runUserSearch],
  );

  const onSearchClientsError = useCallback(() => {
    enqueueSnackbar(t('reconFlags.searchError'), { variant: 'error' });
  }, [enqueueSnackbar, t]);

  const accountIdsFilter = useMemo(
    () => buildReconFlagsAccountIdsFromClientSelection(selectedClient),
    [selectedClient],
  );

  const gridFilter = useMemo(
    () =>
      buildReconFlagsGridFilter({
        orgId,
        flagState,
        flagType,
        severityTab,
        categoryFilter,
        agingFilter,
        accountIds: accountIdsFilter,
      }),
    [
      orgId,
      flagState,
      flagType,
      agingFilter,
      severityTab,
      categoryFilter,
      accountIdsFilter,
    ],
  );

  const { data: flagTypesData } = useQuery(FETCH_FLAG_TYPES);
  const flagTypes = useMemo(() => {
    const names =
      flagTypesData?.__type?.enumValues?.map(
        (enumValue: { name: string }) => enumValue.name,
      ) ?? [];
    return names as string[];
  }, [flagTypesData]);

  const onSeverityTabChange = useCallback(
    (tab: 'ALL' | ReconFlagSeverity) => {
      setSeverityTabStored(tab);
      setPage(1);
    },
    [setPage, setSeverityTabStored],
  );

  const onFlagStateChange = useCallback(
    (value: string) => {
      setFlagState(value);
      setPage(1);
    },
    [setFlagState, setPage],
  );

  const onFlagTypeChange = useCallback(
    (value: string | undefined) => {
      setFlagType(value);
      setPage(1);
    },
    [setFlagType, setPage],
  );

  const onCategoryFilterChange = useCallback(
    (value: string) => {
      setCategoryFilter(value);
      setPage(1);
    },
    [setCategoryFilter, setPage],
  );

  const onAgingFilterChange = useCallback(
    (value: string) => {
      setAgingFilter(value);
      setPage(1);
    },
    [setAgingFilter, setPage],
  );

  const onSelectedClientChange = useCallback(
    (client: ReconFlagsClientOption | null) => {
      setSelectedClient(client);
      setPage(1);
    },
    [setPage],
  );

  const hasActiveFilters = useMemo(
    () =>
      hasReconFlagsActiveFilters({
        severityTab,
        flagState,
        flagType,
        categoryFilter,
        agingFilter,
        selectedClientId: selectedClient?.id ?? null,
      }),
    [
      severityTab,
      flagState,
      flagType,
      categoryFilter,
      agingFilter,
      selectedClient,
    ],
  );

  const onClearFilters = useCallback(() => {
    setFlagState(RECON_FLAGS_DEFAULT_FILTER_VALUES.flagState);
    setFlagType(RECON_FLAGS_DEFAULT_FILTER_VALUES.flagType);
    setSeverityTabStored(RECON_FLAGS_DEFAULT_FILTER_VALUES.severityTab);
    setCategoryFilter(RECON_FLAGS_DEFAULT_FILTER_VALUES.categoryFilter);
    setAgingFilter(RECON_FLAGS_DEFAULT_FILTER_VALUES.agingFilter);
    setSelectedClient(null);
    setPage(1);
  }, [
    setAgingFilter,
    setCategoryFilter,
    setFlagState,
    setFlagType,
    setPage,
    setSeverityTabStored,
  ]);

  const filtersProps = useMemo(
    (): Omit<ReconFlagsFiltersCardProps, 'theme'> => ({
      severityTab,
      onSeverityTabChange,
      flagState,
      onFlagStateChange,
      flagType,
      onFlagTypeChange,
      flagTypes,
      categoryFilter,
      onCategoryFilterChange,
      agingFilter,
      onAgingFilterChange,
      selectedClient,
      onSelectedClientChange,
      onSearchClients,
      onSearchClientsError,
      clientSearchDebounceMs: RECON_FLAGS_SEARCH_DEBOUNCE_MS,
      clientSearchMinChars: RECON_FLAGS_MIN_SEARCH_LENGTH,
      hasActiveFilters,
      onClearFilters,
    }),
    [
      severityTab,
      onSeverityTabChange,
      flagState,
      onFlagStateChange,
      flagType,
      onFlagTypeChange,
      flagTypes,
      categoryFilter,
      onCategoryFilterChange,
      agingFilter,
      onAgingFilterChange,
      selectedClient,
      onSelectedClientChange,
      onSearchClients,
      onSearchClientsError,
      hasActiveFilters,
      onClearFilters,
    ],
  );

  return {
    gridFilter,
    filtersProps,
  };
};
